package com.milta.web

import androidx.compose.runtime.Composable
import com.milta.core.localization.LocalStrings
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text

// Milta charte graphique (voir shared/.../core/ui/theme/Color.kt) — redéclarée en constantes CSS
// brutes ici comme dans les autres écrans web (WebWelcomeScreen.kt, WebAuthScreens.kt) : Compose
// HTML n'a pas accès à MiltaTheme (Compose UI/canvas), et property() est la seule surface stable.
private const val PRIMARY = "#2E5F7A" // BlueDeep
private const val PRIMARY_MID = "#4A90A4" // BlueMid
private const val TEAL = "#5CC8B8"
private const val TEXT_PRIMARY = "#1E3A4A"
private const val TEXT_SECONDARY = "#6B8A9A"
private const val SURFACE_DIM = "#E8EFF3"
private const val BORDER = "#D7DFE3"
private const val ACTIVE_TINT = "#E3F5F2" // Teal ~14% sur fond blanc, calculé à la main (pas de color-mix())

/**
 * Les 4 destinations de la nav principale du dashboard (MIL-47/DOM-06). "Paramètres" (MIL-53)
 * n'en fait pas partie : épinglé au pied de la sidebar, en dehors de cette liste (voir mockup
 * `docs/tickets/MIL-50/attachements/dashboard.png`).
 */
enum class DashboardDestination(val ticketRef: String) {
    TOURNEES_DU_JOUR("MIL-48"),
    AJOUTER_UNE_TOURNEE("MIL-50 / MIL-51"),
    EQUIPE("MIL-49"),
    HISTORIQUE_DES_PREUVES("MIL-52"),
}

/**
 * Shell commun à toutes les pages post-connexion du dashboard web (MIL-47/DOM-06) : cellule logo
 * (bordée à droite ET en bas, distincte de la nav en dessous et du header à droite — voir capture
 * d'écran), nav principale à 4 destinations, pied de sidebar (Paramètres + profil connecté),
 * header dynamique par page, zone de contenu. Layout validé par mockup avant implémentation.
 *
 * [organizationName] est nullable : aucune API n'expose aujourd'hui le nom de l'entreprise de
 * livraison pour le compte connecté (`ProfileResponse` ne porte que firstName/lastName/roles) —
 * le badge est simplement masqué tant que cette donnée n'existe pas côté backend, plutôt que
 * d'afficher une valeur inventée.
 *
 * [active] est nullable : `null` masque entièrement les 4 destinations Enterprise de [mainNav]'s
 * default (compte Admin, 2026-10-02 — voir [mainNav]).
 */
@Composable
fun DashboardShell(
    active: DashboardDestination?,
    headerTitle: String,
    headerSubtitle: String?,
    organizationName: String?,
    userDisplayName: String,
    userRoleLabel: String,
    onNavigate: (DashboardDestination) -> Unit,
    onSettingsClick: () -> Unit,
    onLogout: () -> Unit,
    // MIL-53 — true while the pinned "Paramètres" item's own screen is shown, a destination
    // deliberately outside [DashboardDestination] (see enum doc); kept as a separate flag rather
    // than folded into `active` so the 4-destination `when` callers stay exhaustive/unchanged.
    settingsActive: Boolean = false,
    // Admin skeleton (2026-10-03) — overridable nav slot so a non-Enterprise caller (Admin, with
    // its own destination enum) can render its own [NavItem] rows instead of the 4 Enterprise ones
    // below, without this shell knowing anything about Admin's destinations. Default preserves the
    // exact pre-existing Enterprise behavior (4 fixed items, hidden when `active == null`).
    mainNav: @Composable () -> Unit = {
        val strings = LocalStrings.current
        if (active != null) {
            NavItem(strings.common.navTourneesDuJour, "🚚", active == DashboardDestination.TOURNEES_DU_JOUR) {
                onNavigate(DashboardDestination.TOURNEES_DU_JOUR)
            }
            NavItem(strings.common.navAjouterUneTournee, "➕", active == DashboardDestination.AJOUTER_UNE_TOURNEE) {
                onNavigate(DashboardDestination.AJOUTER_UNE_TOURNEE)
            }
            NavItem(strings.team.screenTitle, "👥", active == DashboardDestination.EQUIPE) {
                onNavigate(DashboardDestination.EQUIPE)
            }
            NavItem(strings.common.navHistoriqueDesPreuves, "📷", active == DashboardDestination.HISTORIQUE_DES_PREUVES) {
                onNavigate(DashboardDestination.HISTORIQUE_DES_PREUVES)
            }
        }
    },
    content: @Composable () -> Unit,
) {
    val strings = LocalStrings.current
    WebSpinnerStyle()

    Div(
        attrs = {
            style {
                property("display", "grid")
                property("grid-template-columns", "250px 1fr")
                property("grid-template-rows", "76px 1fr")
                property("grid-template-areas", "\"logo header\" \"navcol content\"")
                property("min-height", "100vh")
                width(100.percent)
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("grid-area", "logo")
                    property("display", "flex")
                    property("align-items", "center")
                    property("border-right", "1px solid $BORDER")
                    property("border-bottom", "1px solid $BORDER")
                    property("background", "#FFFFFF")
                    padding(0.px, 20.px)
                }
            },
        ) {
            // Real brand logo (same asset as the login screen) — replaces the earlier placeholder
            // ("M" square + "milta" text), which was only a stand-in pending a real icon set.
            Img(
                src = drawableUri("milta_full_logo.png"),
                alt = "Milta",
                attrs = { style { height(28.px) } },
            )
        }

        Div(
            attrs = {
                style {
                    property("grid-area", "navcol")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("border-right", "1px solid $BORDER")
                    property("background", "#FFFFFF")
                    padding(20.px, 16.px)
                    height(100.percent)
                    property("box-sizing", "border-box")
                }
            },
        ) {
            if (organizationName != null) {
                Div(
                    attrs = {
                        style {
                            property("font-size", "10.5px")
                            property("font-weight", "700")
                            property("letter-spacing", "0.06em")
                            property("text-transform", "uppercase")
                            property("color", TEXT_SECONDARY)
                            padding(14.px, 8.px)
                        }
                    },
                ) { Text(organizationName) }
            }

            mainNav()

            Div(attrs = { style { property("margin-top", "auto") } }) {
                Div(attrs = { style { height(1.px); property("background", BORDER); property("margin", "14px 4px") } })
                NavItem(strings.common.navParametres, "⚙️", active = settingsActive, onClick = onSettingsClick)
                LogoutNavItem(label = LocalStrings.current.profile.signOut, onClick = onLogout)
                Div(
                    attrs = {
                        style {
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "10px")
                            padding(10.px, 8.px, 4.px, 8.px)
                            property("margin-top", "6px")
                        }
                    },
                ) {
                    Div(
                        attrs = {
                            style {
                                width(32.px)
                                height(32.px)
                                property("border-radius", "50%")
                                property("background", avatarColorFor(initials(userDisplayName)))
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("flex-shrink", "0")
                                property("color", "#FFFFFF")
                                property("font-weight", "700")
                                property("font-size", "12.5px")
                            }
                        },
                    ) { Text(initials(userDisplayName)) }
                    Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("line-height", "1.25") } }) {
                        Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "600"); property("color", TEXT_PRIMARY) } }) {
                            Text(userDisplayName)
                        }
                        Div(attrs = { style { property("font-size", "11.5px"); property("color", TEXT_SECONDARY) } }) {
                            Text(userRoleLabel)
                        }
                    }
                }
            }
        }

        Div(
            attrs = {
                style {
                    property("grid-area", "header")
                    property("display", "flex")
                    property("align-items", "center")
                    property("border-bottom", "1px solid $BORDER")
                    property("background", "#FFFFFF")
                    padding(0.px, 28.px)
                }
            },
        ) {
            Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "2px") } }) {
                Div(attrs = { style { property("font-size", "19px"); property("font-weight", "700"); property("color", TEXT_PRIMARY) } }) {
                    Text(headerTitle)
                }
                if (headerSubtitle != null) {
                    Div(attrs = { style { property("font-size", "12.5px"); property("color", TEXT_SECONDARY) } }) {
                        Text(headerSubtitle)
                    }
                }
            }
        }

        Div(
            attrs = {
                style {
                    property("grid-area", "content")
                    property("background", SURFACE_DIM)
                    property("overflow", "auto")
                    padding(22.px)
                    property("box-sizing", "border-box")
                }
            },
        ) { content() }
    }
}

// Internal, not private: reused by Main.kt's Admin `mainNav` override (its own destination enum,
// see DashboardShell's `mainNav` param doc).
@Composable
internal fun NavItem(label: String, icon: String, active: Boolean, onClick: () -> Unit) {
    Div(
        attrs = {
            onClick { onClick() }
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "11px")
                padding(10.px, 12.px)
                property("border-radius", "10px")
                property("font-size", "14px")
                property("cursor", "pointer")
                property("margin-bottom", "2px")
                property("border-left", if (active) "3px solid $TEAL" else "3px solid transparent")
                property("background", if (active) ACTIVE_TINT else "transparent")
                property("color", if (active) PRIMARY else TEXT_SECONDARY)
                property("font-weight", if (active) "700" else "500")
                property("box-sizing", "border-box")
            }
        },
    ) {
        // Emoji glyph — pas de dépendance SVG/Material Icons dans Compose HTML (webApp est du DOM
        // pur, pas de backend Skia/Canvas, voir [[documentation:webapp-target-is-kotlin-js-not-wasm]]).
        Div(
            attrs = {
                style {
                    width(20.px)
                    property("font-size", "16px")
                    property("line-height", "1")
                    property("text-align", "center")
                    property("flex-shrink", "0")
                }
            },
        ) { Text(icon) }
        Text(label)
    }
}

// Not a [DashboardDestination]/settings toggle — a plain action row pinned right under
// "Paramètres" (mirrors mobile ProfileScreen's own "Se déconnecter" row: red label, no
// active/highlight state since it never represents "where you are").
@Composable
private fun LogoutNavItem(label: String, onClick: () -> Unit) {
    Div(
        attrs = {
            onClick { onClick() }
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "11px")
                padding(10.px, 12.px)
                property("border-radius", "10px")
                property("font-size", "14px")
                property("cursor", "pointer")
                property("margin-bottom", "2px")
                property("border-left", "3px solid transparent")
                property("color", "#D32F2F")
                property("font-weight", "600")
                property("box-sizing", "border-box")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    width(20.px)
                    property("font-size", "16px")
                    property("line-height", "1")
                    property("text-align", "center")
                    property("flex-shrink", "0")
                }
            },
        ) { Text("🚪") }
        Text(label)
    }
}

internal fun initials(name: String): String =
    name.trim().split(Regex("\\s+")).mapNotNull { it.firstOrNull()?.uppercaseChar() }.take(2).joinToString("")

// Fixed palette of brand-adjacent hues, distinct enough to tell people apart at a glance.
// Deliberately NOT a per-letter/per-name lookup table (would need updating for every new person) —
// the initials string is hashed into the palette instead, so any combination of letters gets a
// stable color "for free": same person always renders the same color, different initials usually
// render differently (palette-size-many possible colors, occasional collision is fine — same idea
// as Slack/GitHub-style avatar coloring).
private val AvatarPalette = listOf(
    "#2E5F7A", "#4A90A4", "#5CC8B8", "#3E8E7E",
    "#8FDB8F", "#C9820A", "#8E44AD", "#4C6EF5",
)

internal fun avatarColorFor(initials: String): String {
    val index = kotlin.math.abs(initials.hashCode()) % AvatarPalette.size
    return AvatarPalette[index]
}
